import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import NumberInput from 'components/form/managed_input/number_input';

import { MAX_RADII, MAX_RADIUS, MIN_RADIUS, RADIUS_STEP } from './demographics_constants';
import { CLASSIC_RING_COLOR, CLASSIC_RING_FALLBACK_COLOR, classicRingOpacity } from './ring_styles';
import type { RingEditorProps } from './types';

interface RadiusLegendProps extends RingEditorProps {
  radiusColor?: string;
}

const numberInputStyles: React.CSSProperties = {
  width: 'calc(4em + 30px)', // widen content area so spinners shift right
  marginRight: '10px',
  boxSizing: 'content-box',
  fontSize: '17px',
  borderRadius: '6px'
};

export default function RadiusLegend({
  radiusColor = CLASSIC_RING_COLOR,
  radiusRings,
  onAddRadiusRing,
  onRadiusRingChange,
  onRemoveRadiusRing
}: RadiusLegendProps) {
  const canRemove = radiusRings.length > 1;
  const canAdd = radiusRings.length < MAX_RADII;

  return (
    <div
      className="map-legend"
      style={{
        position: 'absolute',
        bottom: '30px',
        right: '10px',
        backgroundColor: 'white',
        padding: '8px 16px',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        fontSize: '14px',
        zIndex: 1000,
        width: '280px',
        maxWidth: 'calc(100% - 20px)',
        maxHeight: '70%',
        overflowY: 'auto'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ fontWeight: 'bold' }}>Radius Rings</div>
      </div>
      <p> Changes made to the radius rings will be reflected in your documents.</p>
      {radiusRings.map((radius, index) => {
        const opacity = classicRingOpacity(index);

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginBottom: '12px'
            }}
          >
            <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Radius {index + 1}</p>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: radiusColor || CLASSIC_RING_FALLBACK_COLOR,
                  opacity: opacity,
                  borderRadius: '50%',
                  marginRight: '8px',
                  border: `1px solid ${radiusColor || '#2563EB'}`,
                  flexShrink: 0
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <NumberInput
                  className="demographics-radius-input"
                  inputMode="decimal"
                  maxValue={MAX_RADIUS}
                  minValue={MIN_RADIUS}
                  pattern="[0-9]*[.]?[0-9]+"
                  step={RADIUS_STEP}
                  style={numberInputStyles}
                  value={radius}
                  onChange={() => {}}
                  onValueChange={v => onRadiusRingChange(index, v)}
                />
                <span>Miles</span>
                {canRemove && (
                  <button
                    aria-label={`Remove Radius ${index + 1}`}
                    className="btn btn-link p-0 ms-auto"
                    style={{
                      color: '#6b7280',
                      cursor: 'pointer',
                      fontSize: '16px',
                      lineHeight: 1,
                      marginLeft: 'auto'
                    }}
                    title="Remove this radius"
                    type="button"
                    onClick={() => onRemoveRadiusRing(index)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {canAdd && (
        <button
          className="btn btn-link p-0"
          style={{ fontWeight: 600, textDecoration: 'none' }}
          type="button"
          onClick={onAddRadiusRing}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
          Add Radius
        </button>
      )}
    </div>
  );
}
